import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { adminApi } from "@/lib/adminApi";
import { Field } from "@/pages/admin/Field";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Trash2, Plus, Upload, Pencil, MousePointerClick } from "lucide-react";
import { toast } from "sonner";

const SLOT_LABEL = { top: "Top banner (home)", mid: "Mid banner (home)", sidebar: "Sidebar (all pages)" };
const mediaUrl = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : "");

const AdCard = ({ ad, onSaved }) => {
  const [form, setForm] = useState(ad);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    try { await adminApi.updateAd(ad.id, form); toast.success("Ad updated"); onSaved(); }
    catch { toast.error("Save failed"); }
  };
  return (
    <div className="space-y-3 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
      <p className="text-sm font-semibold text-gmoaa-green">{SLOT_LABEL[ad.slot] || ad.slot}</p>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Title (English)" value={form.title_en} onChange={set("title_en")} />
        <Field label="Title (Gujarati)" value={form.title_gu} onChange={set("title_gu")} />
        <Field label="Subtitle (English)" value={form.subtitle_en} onChange={set("subtitle_en")} />
        <Field label="Subtitle (Gujarati)" value={form.subtitle_gu} onChange={set("subtitle_gu")} />
        <div>
          <p className="mb-1 text-xs font-medium text-gmoaa-text-muted">Color</p>
          <Select value={form.color} onValueChange={set("color")}>
            <SelectTrigger className="border-gmoaa-border-subtle bg-white"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="gold">Gold</SelectItem><SelectItem value="green">Green</SelectItem><SelectItem value="saffron">Saffron</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 pt-5">
          <Switch checked={!!form.active} onCheckedChange={set("active")} />
          <Label className="text-sm">Active</Label>
        </div>
      </div>
      <Button onClick={save} className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save</Button>
    </div>
  );
};

// ---- Advertisement pricing (rate card) editor ----
const PricingEditor = () => {
  const [slots, setSlots] = useState([]);
  const load = () => adminApi.adRateCard().then((d) => setSlots(d.slots || []));
  useEffect(() => { load(); }, []);
  const setSlot = (i, k, v) => setSlots((arr) => arr.map((s, idx) => (idx === i ? { ...s, [k]: v } : s)));
  const save = async () => {
    try { await adminApi.updateAdRateCard({ slots }); toast.success("Pricing updated"); load(); }
    catch { toast.error("Save failed"); }
  };
  return (
    <div className="space-y-4" data-testid="ad-pricing-editor">
      {slots.map((s, i) => (
        <div key={s.id} className="space-y-3 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
          <p className="text-sm font-semibold text-gmoaa-green">{s.slot_en}</p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Slot name (English)" value={s.slot_en} onChange={(v) => setSlot(i, "slot_en", v)} />
            <Field label="Slot name (Gujarati)" value={s.slot_gu} onChange={(v) => setSlot(i, "slot_gu", v)} />
            <Field label="Placement (English)" value={s.placement_en} onChange={(v) => setSlot(i, "placement_en", v)} />
            <Field label="Placement (Gujarati)" value={s.placement_gu} onChange={(v) => setSlot(i, "placement_gu", v)} />
            <Field label="Amount (₹, 0 = On request)" type="number" value={s.amount} onChange={(v) => setSlot(i, "amount", v)} testid={`rate-amount-${s.id}`} />
            <Field label="Period label (English)" value={s.period_en} onChange={(v) => setSlot(i, "period_en", v)} placeholder="/ month" />
            <Field label="Period label (Gujarati)" value={s.period_gu} onChange={(v) => setSlot(i, "period_gu", v)} placeholder="/ માસ" />
          </div>
        </div>
      ))}
      <Button onClick={save} data-testid="ad-pricing-save" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save pricing</Button>
    </div>
  );
};

// ---- GAPL match-centre sponsors manager ----
const GaplSponsors = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ name_en: "", name_gu: "", logo_url: "", link: "" });
  const [uploading, setUploading] = useState(false);
  const [editing, setEditing] = useState(null);
  const load = () => adminApi.gaplSponsors().then(setRows);
  useEffect(() => { load(); }, []);

  const onLogo = async (e, setter) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    if (!["image/png", "image/jpeg"].includes(file.type)) { toast.error("Only PNG or JPG allowed"); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error("Image must be 2 MB or smaller"); return; }
    setUploading(true);
    try { const r = await adminApi.uploadSponsorLogo(file); setter(r.url); toast.success("Logo uploaded"); }
    catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  const add = async () => {
    if (!form.name_en.trim()) { toast.error("Sponsor name (English) is required"); return; }
    if (form.link && !/^https?:\/\//.test(form.link)) { toast.error("Link must start with http:// or https://"); return; }
    try { await adminApi.createGaplSponsor(form); toast.success("Sponsor added"); setForm({ name_en: "", name_gu: "", logo_url: "", link: "" }); load(); }
    catch { toast.error("Save failed"); }
  };
  const remove = async (id) => { await adminApi.deleteGaplSponsor(id); toast.success("Removed"); load(); };

  return (
    <div className="space-y-4" data-testid="gapl-sponsors-admin">
      <div className="space-y-2">
        {rows.map((s) => (
          <div key={s.id} className="flex items-center justify-between rounded-xl border border-gmoaa-border-subtle bg-white p-3">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-20 items-center justify-center overflow-hidden rounded border border-gmoaa-border-subtle bg-gmoaa-surface">
                {s.logo_url ? <img src={mediaUrl(s.logo_url)} alt="" className="max-h-full max-w-full object-contain" /> : <span className="text-[10px] text-gmoaa-text-muted">No logo</span>}
              </div>
              <div className="min-w-0">
                <p className="font-medium text-gmoaa-text-dark">{s.name_en}</p>
                <p className="truncate text-xs text-gmoaa-text-muted">{s.link ? s.link : "No link"}</p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <span className="flex items-center gap-1 rounded-full bg-gmoaa-green-light px-2 py-0.5 text-xs font-semibold text-gmoaa-green" data-testid={`sponsor-clicks-${s.id}`}>
                <MousePointerClick size={12} /> {s.clicks || 0}
              </span>
              <Button size="sm" variant="ghost" onClick={() => setEditing(s)} data-testid={`gapl-sponsor-edit-${s.id}`}><Pencil size={15} /></Button>
              <Button size="sm" variant="ghost" className="text-gmoaa-red" onClick={() => remove(s.id)} data-testid={`gapl-sponsor-del-${s.id}`}><Trash2 size={16} /></Button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-gmoaa-text-muted">No match-centre sponsors yet.</p>}
      </div>

      <div className="space-y-3 rounded-xl border border-dashed border-gmoaa-border-strong bg-white p-5">
        <p className="text-sm font-semibold text-gmoaa-green">Add sponsor</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Name (English)" value={form.name_en} onChange={(v) => setForm((f) => ({ ...f, name_en: v }))} testid="gapl-sponsor-name-en" />
          <Field label="Name (Gujarati)" value={form.name_gu} onChange={(v) => setForm((f) => ({ ...f, name_gu: v }))} />
          <Field label="Website link (optional, tracked)" value={form.link} onChange={(v) => setForm((f) => ({ ...f, link: v }))} placeholder="https://…" testid="gapl-sponsor-link" />
        </div>
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-20 items-center justify-center overflow-hidden rounded border border-gmoaa-border-subtle bg-gmoaa-surface">
            {form.logo_url ? <img src={mediaUrl(form.logo_url)} alt="" className="max-h-full max-w-full object-contain" /> : <span className="text-[10px] text-gmoaa-text-muted">Preview</span>}
          </div>
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gmoaa-green bg-white px-3 py-2 text-xs font-medium text-gmoaa-green hover:bg-gmoaa-green-light">
            <Upload size={14} /> {uploading ? "Uploading…" : "Upload logo (PNG/JPG ≤2MB)"}
            <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => onLogo(e, (u) => setForm((f) => ({ ...f, logo_url: u })))} disabled={uploading} data-testid="gapl-sponsor-logo-input" />
          </label>
        </div>
        <Button onClick={add} data-testid="gapl-sponsor-add" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover"><Plus size={15} className="mr-1" /> Add sponsor</Button>
      </div>

      {editing && <EditSponsorDialog s={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} onLogo={onLogo} uploading={uploading} />}
    </div>
  );
};

const EditSponsorDialog = ({ s, onClose, onSaved, onLogo, uploading }) => {
  const [form, setForm] = useState({ name_en: s.name_en || "", name_gu: s.name_gu || "", logo_url: s.logo_url || "", link: s.link || "" });
  const save = async () => {
    if (!form.name_en.trim()) { toast.error("Name (English) is required"); return; }
    if (form.link && !/^https?:\/\//.test(form.link)) { toast.error("Link must start with http:// or https://"); return; }
    try { await adminApi.updateGaplSponsor(s.id, form); toast.success("Sponsor updated"); onSaved(); }
    catch { toast.error("Save failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-gmoaa-cream">
        <DialogHeader><DialogTitle className="text-gmoaa-green">Edit sponsor</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Name (English)" value={form.name_en} onChange={(v) => setForm((f) => ({ ...f, name_en: v }))} testid="edit-sponsor-name-en" />
          <Field label="Name (Gujarati)" value={form.name_gu} onChange={(v) => setForm((f) => ({ ...f, name_gu: v }))} />
          <Field label="Website link (tracked)" value={form.link} onChange={(v) => setForm((f) => ({ ...f, link: v }))} placeholder="https://…" testid="edit-sponsor-link" />
        </div>
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-20 items-center justify-center overflow-hidden rounded border border-gmoaa-border-subtle bg-gmoaa-surface">
            {form.logo_url ? <img src={mediaUrl(form.logo_url)} alt="" className="max-h-full max-w-full object-contain" /> : <span className="text-[10px] text-gmoaa-text-muted">No logo</span>}
          </div>
          <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gmoaa-green bg-white px-3 py-2 text-xs font-medium text-gmoaa-green hover:bg-gmoaa-green-light">
            <Upload size={14} /> {uploading ? "Uploading…" : "Replace logo"}
            <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={(e) => onLogo(e, (u) => setForm((f) => ({ ...f, logo_url: u })))} disabled={uploading} />
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-gmoaa-border-subtle">Cancel</Button>
          <Button onClick={save} data-testid="edit-sponsor-save" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AdsSection = () => {
  const [rows, setRows] = useState([]);
  const load = () => api.ads().then(setRows);
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-10">
      <div>
        <h2 className="mb-5 font-serif text-2xl font-semibold text-gmoaa-green">Ad Slots</h2>
        <div className="space-y-4">
          {rows.map((ad) => <AdCard key={ad.id} ad={ad} onSaved={load} />)}
        </div>
      </div>
      <div>
        <h2 className="mb-5 font-serif text-2xl font-semibold text-gmoaa-green">Advertisement Pricing</h2>
        <p className="mb-4 text-sm text-gmoaa-text-muted">These rates appear live on the public Advertise page. Set amount to 0 to show “On request”.</p>
        <PricingEditor />
      </div>
      <div>
        <h2 className="mb-5 font-serif text-2xl font-semibold text-gmoaa-green">GAPL Match-Centre Sponsors</h2>
        <p className="mb-4 text-sm text-gmoaa-text-muted">Shown on the GAPL hub and the Advertise page. Add as many as you like.</p>
        <GaplSponsors />
      </div>
    </div>
  );
};
